/**
 * 포트폴리오 생성 플로우 추적 훅
 * 
 * 포트폴리오 생성 전체 플로우의 의도-결과를 추적
 * - portfolio_flow_start: 포트폴리오 생성 시작
 * - portfolio_flow_success: 포트폴리오 생성 성공
 * - portfolio_flow_fail: 포트폴리오 생성 실패
 * 
 * @example
 * ```tsx
 * function PortfolioPage() {
 *   const portfolioFlow = usePortfolioFlow();
 *   
 *   useEffect(() => {
 *     // 포트폴리오 생성 플로우 시작
 *     portfolioFlow.start();
 *   }, []);
 *   
 *   const handleComplete = async () => {
 *     try {
 *       const result = await createPortfolio(data);
 *       portfolioFlow.success({ portfolio_id: result.id });
 *     } catch (error) {
 *       portfolioFlow.fail('backend_5xx');
 *     }
 *   };
 * }
 * ```
 */

import { useIntent } from '../useIntent';
import { track } from '../../../../core/client';

export interface PortfolioFlowContext {
    portfolio_id?: number;
}

export interface PortfolioFlowActions {
    start: (options?: {
        parentIntentId?: string;
        parentIntentType?: string;
    }) => string;
    success: (context?: PortfolioFlowContext) => void;
    fail: (reason?: string) => void;
}

/**
 * 포트폴리오 생성 플로우 추적 훅
 */
export function usePortfolioFlow(): PortfolioFlowActions {
    const intent = useIntent<PortfolioFlowContext>({
        type: 'portfolio_flow',
        requiredGate: 'none',  // 비회원도 가능
        onStart: ({ intent_id, parent_intent_id, required_gate }) => {
            track('portfolio_flow_start', {
                intent_id,
                parent_intent_id,
                required_gate,
            });
        },
        onSuccess: ({ intent_id, context, latency_ms }) => {
            track('portfolio_flow_success', {
                intent_id,
                portfolio_id: context?.portfolio_id,
                latency_ms,
            });
        },
        onFail: ({ intent_id, fail_reason }) => {
            track('portfolio_flow_fail', {
                intent_id,
                fail_reason,
            });
        },
    });



    return {
        start: (options) => intent.start(undefined, options),
        success: (context) => intent.success(),
        fail: (reason) => intent.fail(reason),
    };
}

